import {
    getCustomerFlow,
    getFlowType,
    getLoginData,
    getSiteStructure,
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_PRODUCT_LINE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_SITE_AREA_MOBILITY,
    TrackingBtx,
    TrackingFlowType,
    TrackingInformation,
    PageViewTrackingData,
    TrackingProduct,
    TrackToggleOption,
    TrackType,
    TrackingLoginStatus,
    TrackingProductType,
    TrackingPageType,
    TrackingInformationUiType,
    TrackingInformationTrigger,
    TrackingError,
    PageViewTrackingDataWithError,
} from '@vfde-sails/tracking';
import {
    ADDITIONAL_PAGE_OPTIONS,
    BTX_BNT,
} from '@vfde-sails/constants';
import { getTradeInDiscountSoc } from '@vfde-sails/page-options';
import { selectPageViewTrackingData } from '../features/app/selectors';
import { Device } from '../features/tradeIn/interfaces/api';

/**
 * Create the page view tracking object
 */
export const createPageViewTrackingData = ({
    customerConfig,
    subscription,
    monthlyPriceToPay,
    atomic: atomicDevice,
}: ReturnType<typeof selectPageViewTrackingData>): PageViewTrackingData => {
    const { flowType, isHardwareOnly, salesChannel, subscriptionId } = customerConfig;
    const { pageName } = window[ADDITIONAL_PAGE_OPTIONS];
    const siteStructure = getSiteStructure(pageName, BTX_BNT);

    /* istanbul ignore next */
    const loginStatus = getLoginData() ? TrackingLoginStatus.LoggedIn : TrackingLoginStatus.NotLoggedIn;
    const lineOfCustomer = getCustomerFlow(salesChannel);

    let oOrder: PageViewTrackingData['oOrder'];

    if (atomicDevice) {
        const {
            atomicName,
            priceHardwareOnly,
            priceHardwareOneTimeWithTariff,
        } = atomicDevice;

        const productCategory = isHardwareOnly ? TrackingProductType.Device : TrackingProductType.Bundle;

        const devicePriceOnce = isHardwareOnly
            ? priceHardwareOnly.gross
            : priceHardwareOneTimeWithTariff.gross;

        const aProducts: TrackingProduct[] = [{
            sku: atomicDevice.sku,
            name: atomicName,
            type: TrackingProductType.Device,
            priceOnce: devicePriceOnce.toString(),
            proposition: 1,
            units: 1,
        }];

        let propositionName = atomicName;

        if (subscription && subscriptionId && !isHardwareOnly) {
            const connectionFee = subscription!.priceConnectionFee;

            /* istanbul ignore next */
            const priceOnce = (connectionFee?.gross || 0).toString();

            aProducts.push({
                sku: subscriptionId,
                name: subscription!.tariffName,
                type: TrackingProductType.Tariff,
                priceOnce,
                priceMonthly: monthlyPriceToPay?.gross.toString(),
                duration: 24,
                proposition: 1,
                units: 1,
            });

            propositionName += ` | ${subscription!.tariffName}`;
        }

        oOrder = {
            aProducts,
            aPropositions: [{
                id: 1,
                name: propositionName,
                targetAudience: lineOfCustomer,
                retention: isHardwareOnly ? TrackingBtx.HardwareOnly : TrackingBtx.Bnt,
                productType: isHardwareOnly ? TrackingBtx.HardwareOnly : TRACKING_PRODUCT_TYPE,
                productLine: isHardwareOnly ? TrackingBtx.HardwareOnly : TRACKING_PRODUCT_LINE,
                productCategory,
                units: 1,
            }],
        };
    }

    const trackingPayload: PageViewTrackingData = {
        /* eslint-disable jsdoc/require-jsdoc, @typescript-eslint/naming-convention, camelcase */
        page_levels: siteStructure,
        login_status: loginStatus,
        flow_type: isHardwareOnly ? TrackingFlowType.HardwareOnly : getFlowType(flowType, salesChannel),
        line_of_customer: lineOfCustomer,
        page_type: TrackingPageType.ProductDetail,
        site_area: TRACKING_SITE_AREA_MOBILITY,
        platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        business_transaction_type: isHardwareOnly ? TrackingBtx.HardwareOnly : TrackingBtx.Bnt,
        /* eslint-enable jsdoc/require-jsdoc, @typescript-eslint/naming-convention, camelcase */
        oOrder,
    };

    return trackingPayload;
};

/**
 * Create the page view tracking object with error
 */
export const createPageViewTrackingDataWithError = (
    trackingData: ReturnType<typeof selectPageViewTrackingData>,
    trackingError: TrackingError,
): PageViewTrackingDataWithError => {
    const pageViewTrackingData = createPageViewTrackingData(trackingData);

    return {
        ...pageViewTrackingData,
        ...trackingError,
    };
};

/**
 * Get tracking payload for trade in
 */
export const getTradeInTrackingPayload = (isTradeInSelected: boolean, tradeInDevice: Device | null): {
    trackingType: TrackType;
    trackingPayload: { [key in TrackToggleOption]?: TrackingProduct[] } | TrackingInformation;
} => {
    if (!tradeInDevice && isTradeInSelected) {
        return {
            trackingType: TrackType.Information,
            trackingPayload: {
                /* eslint-disable @typescript-eslint/naming-convention, camelcase */
                information_action: TRACKING_INFORMATION_ACTION_REVEAL,
                information_name: 'trade-in option',
                information_ui_type: TrackingInformationUiType.Overlay,
                information_trigger: TrackingInformationTrigger.Anchor,
                /* eslint-enable */
            },
        };
    }

    return {
        trackingType: TrackType.SwitchOptions,
        trackingPayload: {
            [isTradeInSelected && tradeInDevice ? TrackToggleOption.Selected : TrackToggleOption.Removed]: [{
                sku: getTradeInDiscountSoc(),
                name: `trade in ${tradeInDevice?.name}`,
                type: TrackingProductType.Discount,
                priceOnce: tradeInDevice?.formattedPrice,
                priceMonthly: '0.00',
                proposition: 1,
                units: 1,
            }],
        },
    };
};
